import React, { useState } from 'react';
import {
  Box,
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Container,
  Card,
  CardContent,
  Menu,
  MenuItem,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  People as PeopleIcon,
  Pets as PetsIcon,
  CalendarToday as CalendarIcon,
  Schedule as ScheduleIcon,
  MedicalServices as MedicalIcon,
  ExitToApp as LogoutIcon,
  AccountCircle,
  LocalHospital,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import GestionProprietaires from './GestionProprietaires';
import GestionAnimaux from './GestionAnimaux';
import PlanningVeterinaire from './PlanningVeterinaire';
import GestionDisponibilites from './GestionDisponibilites';

export const VeterinaireDashboard: React.FC = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [currentView, setCurrentView] = useState('dashboard');
  const { user, logout } = useAuth();

  const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    logout();
    handleProfileMenuClose();
  };

  const menuItems = [
    { id: 'dashboard', label: 'Tableau de bord', icon: <DashboardIcon /> },
    { id: 'proprietaires', label: 'Propriétaires', icon: <PeopleIcon /> },
    { id: 'animaux', label: 'Animaux', icon: <PetsIcon /> },
    { id: 'calendar', label: 'Calendrier RDV', icon: <CalendarIcon /> },
    { id: 'disponibilites', label: 'Disponibilités', icon: <ScheduleIcon /> },
    { id: 'consultations', label: 'Consultations', icon: <MedicalIcon /> },
  ];

  const renderContent = () => {
    switch (currentView) {
      case 'dashboard':
        return (
          <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
            <Typography variant="h4" gutterBottom>
              Tableau de bord vétérinaire
            </Typography>
            <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
              Bienvenue Dr. {user?.first_name} {user?.last_name}
            </Typography>
            <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', mt: 3 }}>
              <Card sx={{ minWidth: 200, flex: 1 }}>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom>
                    RDV aujourd'hui
                  </Typography>
                  <Typography variant="h4">
                    0
                  </Typography>
                </CardContent>
              </Card>
              <Card sx={{ minWidth: 200, flex: 1 }}>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom>
                    Patients suivis
                  </Typography>
                  <Typography variant="h4">
                    0
                  </Typography>
                </CardContent>
              </Card>
              <Card sx={{ minWidth: 200, flex: 1 }}>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom>
                    Consultations ce mois
                  </Typography>
                  <Typography variant="h4">
                    0
                  </Typography>
                </CardContent>
              </Card>
            </Box>
          </Container>
        );
      case 'proprietaires':
        return <GestionProprietaires />;
      case 'animaux':
        return <GestionAnimaux />;
      case 'calendar':
        return (
          <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
            <PlanningVeterinaire />
          </Container>
        );
      case 'disponibilites':
        return (
          <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
            <GestionDisponibilites />
          </Container>
        );
      case 'consultations':
        return (
          <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
            <Typography variant="h4" gutterBottom>
              Consultations
            </Typography>
            <Typography variant="body1">
              Historique et nouvelles consultations...
            </Typography>
          </Container>
        );
      default:
        return null;
    }
  };

  return (
    <Box sx={{ display: 'flex' }}>
      {/* AppBar */}
      <AppBar position="fixed" sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }}>
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            onClick={() => setSidebarOpen(true)}
            edge="start"
            sx={{ mr: 2 }}
          >
            <MenuIcon />
          </IconButton>
          <LocalHospital sx={{ mr: 2 }} />
          <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>
            VetCare - Espace Vétérinaire
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Typography variant="body2" sx={{ mr: 2 }}>
              Dr. {user?.first_name} {user?.last_name}
            </Typography>
            <IconButton
              size="large"
              aria-label="account of current user"
              aria-controls="menu-appbar"
              aria-haspopup="true"
              onClick={handleProfileMenuOpen}
              color="inherit"
            >
              <AccountCircle />
            </IconButton>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Sidebar */}
      <Drawer
        anchor="left"
        open={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
      >
        <Toolbar />
        <Box sx={{ width: 250 }}>
          <List>
            {menuItems.map((item) => (
              <ListItem
                key={item.id}
                onClick={() => {
                  setCurrentView(item.id);
                  setSidebarOpen(false);
                }}
                sx={{ 
                  cursor: 'pointer',
                  backgroundColor: currentView === item.id ? 'action.selected' : 'transparent',
                  '&:hover': {
                    backgroundColor: 'action.hover',
                  },
                }}
              >
                <ListItemIcon>{item.icon}</ListItemIcon>
                <ListItemText primary={item.label} />
              </ListItem>
            ))}
          </List>
        </Box>
      </Drawer>

      {/* Profile Menu */}
      <Menu
        id="menu-appbar"
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        keepMounted
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        open={Boolean(anchorEl)}
        onClose={handleProfileMenuClose}
      >
        <MenuItem onClick={handleLogout}>
          <LogoutIcon sx={{ mr: 1 }} />
          Déconnexion
        </MenuItem>
      </Menu>

      {/* Main Content */}
      <Box component="main" sx={{ flexGrow: 1, p: 3 }}>
        <Toolbar />
        {renderContent()}
      </Box>
    </Box>
  );
}; 